/**
 * OptimizationsPanel — panneau de réglages d'optimisation matérielle.
 *
 * Un contrôle par technique, rendu depuis les métadonnées de
 * /api/optimizations/available (aucune estimation GB codée en dur côté
 * client : impacts affichés textuellement, compatibilité lue sur
 * /api/optimizations/compatibility). Les valeurs vivent dans un objet
 * OptimizationOptions interne, initialisé depuis les defaults du backend,
 * et tout changement notifie onChange + écrit dans le store
 * (store.optimizationOptions) pour les autres workspaces.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { confirmDialog } from "../../components/Modal.js";
import { toast } from "../../components/Toast.js";
import { h, setChildren } from "../../components/dom.js";
import { checkboxField, numberField, selectField, sliderField } from "../../components/fields.js";
import { serverGenerates } from "../../services/execution.js";
import { store } from "../../state/store.js";
import type {
	OptimizationOptions,
	OptimizationPreset,
	OptimizationProfile,
	OptimizationTechnique,
} from "../../types/index.js";

const CATEGORY_META: Record<string, { label: string }> = {
	engine: { label: "Moteur (quantification & périphérique)" },
	memory: { label: "Mémoire (survie)" },
	generation: { label: "Génération (qualité)" },
	speed: { label: "Performance (vitesse)" },
	resolution: { label: "Haute résolution" },
};
const CATEGORY_ORDER = ["engine", "memory", "generation", "speed", "resolution"];

const PRESET_ORDER = [
	"auto",
	"quality",
	"balanced",
	"fast",
	"turbo",
	"flux",
	"maximum_vram",
] as const;
const PRESET_LABELS: Record<string, string> = {
	auto: "Auto",
	quality: "Qualité",
	balanced: "Équilibré",
	fast: "Rapide",
	turbo: "Turbo",
	flux: "Flux",
	maximum_vram: "VRAM max",
	custom: "Personnalisé",
};

// Libellés lisibles des valeurs d'enum (pas de tailles GB : celles-ci
// vivent dans les descriptions fournies par le backend).
const OPTION_LABELS: Record<string, string> = {
	none: "Aucun",
	auto: "Auto",
	float32: "FP32 (lent)",
	float16: "FP16 (rapide)",
	bfloat16: "BF16 (rapide)",
	nf4: "NF4",
	fp4: "FP4",
	int8: "INT8",
	fp8: "FP8",
	cpu: "CPU",
	cuda: "CUDA",
	disk: "Disque",
	on: "Activé",
	off: "Désactivé",
};

const FIELD_OPTION_LABELS: Record<string, Record<string, string>> = {
	cpu_offload: {
		none: "Aucun (tout sur GPU)",
		model: "Model (équilibré)",
		group: "Group (par blocs)",
		sequential: "Sequential (VRAM mini, lent)",
	},
	attention_slicing: {
		off: "Off (pleine vitesse)",
		normal: "Normal",
		max: "Max (VRAM mini)",
	},
};

function optionLabel(fieldId: string, value: string): string {
	return FIELD_OPTION_LABELS[fieldId]?.[value] ?? OPTION_LABELS[value] ?? value;
}

/** Un contrôle rendu + l'input sous-jacent (pour disabled/labels). */
interface ControlEntry {
	input: HTMLInputElement | HTMLSelectElement;
	setValue(value: unknown): void;
}

export class OptimizationsPanel {
	readonly element: HTMLElement;

	private readonly onChange?: (options: OptimizationOptions) => void;
	private techniques: OptimizationTechnique[] = [];
	private presets: Record<string, OptimizationPreset> = {};
	private profiles: OptimizationProfile[] = [];
	private options: OptimizationOptions = {} as OptimizationOptions;
	private activePreset = "auto";
	private ready = false;

	private readonly controls = new Map<string, ControlEntry>();
	private readonly rows = new Map<string, HTMLElement>();
	private readonly presetButtons = new Map<string, HTMLButtonElement>();
	private readonly compatBox: HTMLElement;
	private readonly categoriesContainer: HTMLElement;
	private readonly profilesCard: HTMLElement;
	private readonly profileSelect: HTMLSelectElement;
	private readonly profileNameInput: HTMLInputElement;
	private readonly profileDeleteButton: HTMLButtonElement;
	private readonly profileStatus: HTMLElement;
	private readonly loadingNote: HTMLElement;

	/** Dernières valeurs effective_* rapportées par le serveur (labels "Auto → x"). */
	private effectiveValues: Record<string, unknown> = {};
	/** Jeton anti-course pour les requêtes auto-preset / compatibilité. */
	private archToken = 0;
	private compatToken = 0;

	constructor(options?: { onChange?: (options: OptimizationOptions) => void }) {
		this.onChange = options?.onChange;

		// --- Sélecteur de preset ---
		const presetSelector = h(
			"div",
			{ class: "preset-selector" },
			...[...PRESET_ORDER, "custom" as const].map((name) => {
				const button = h(
					"button",
					{
						class: "btn btn-small preset-btn",
						type: "button",
						title: this.presets[name]?.description ?? PRESET_LABELS[name],
						onclick: () => void this.applyPreset(name),
					},
					PRESET_LABELS[name]
				);
				this.presetButtons.set(name, button);
				return button;
			})
		);
		const resetButton = h(
			"button",
			{
				class: "btn btn-small",
				type: "button",
				title: "Revenir aux valeurs par défaut de chaque technique",
				onclick: () => this.resetToDefaults(),
			},
			"Réinitialiser"
		);
		const presetCard = h(
			"div",
			{ class: "card" },
			h("div", { class: "card-title opt-presets-title" }, h("span", null, "Presets"), resetButton),
			presetSelector
		);

		this.compatBox = h("div", { class: "compat-panel", hidden: true });
		this.categoriesContainer = h("div", { class: "opt-categories" });
		this.loadingNote = h("p", { class: "muted" }, "Chargement des techniques…");

		// --- Profils ---
		this.profileSelect = h("select", {
			onchange: () => void this.applySelectedProfile(),
		}) as HTMLSelectElement;
		this.profileNameInput = h("input", {
			type: "text",
			placeholder: "Nom du profil",
		}) as HTMLInputElement;
		const profileSaveButton = h(
			"button",
			{
				class: "btn btn-small",
				type: "button",
				onclick: () => void this.saveProfile(),
			},
			"Enregistrer"
		);
		this.profileDeleteButton = h(
			"button",
			{
				class: "btn btn-small btn-danger",
				type: "button",
				disabled: true,
				onclick: () => void this.deleteProfile(),
			},
			"Supprimer"
		) as HTMLButtonElement;
		this.profileStatus = h("p", { class: "help-text" });
		this.profilesCard = h(
			"div",
			{ class: "card" },
			h("div", { class: "card-title" }, "Profils enregistrés"),
			h(
				"div",
				{ class: "profile-controls" },
				this.profileSelect,
				this.profileNameInput,
				profileSaveButton,
				this.profileDeleteButton
			),
			this.profileStatus
		);

		this.element = h(
			"div",
			{ class: "opt-panel" },
			presetCard,
			this.compatBox,
			this.categoriesContainer,
			this.loadingNote,
			this.profilesCard
		);

		this.setActivePreset(this.activePreset);

		// Architecture du modèle choisi : grise les techniques non supportées,
		// rafraîchit la compatibilité, recalcule le preset auto.
		store.subscribe(
			(s) => s.selectedModel?.architecture ?? null,
			(arch) => void this.onArchitectureChange(arch)
		);
		// Les valeurs "effective_*" du serveur alimentent les labels "Auto → x".
		store.subscribe(
			(s) => s.status,
			(status) => {
				this.effectiveValues = (status?.applied_optimizations ?? {}) as Record<string, unknown>;
				this.updateAutoLabels();
			}
		);

		void this.init();
	}

	// ==================== API publique ====================

	getOptions(): OptimizationOptions {
		return { ...this.options, active_preset: this.activePreset };
	}

	setOptions(partial: Partial<OptimizationOptions>): void {
		this.options = { ...this.options, ...partial };
		if (typeof partial.active_preset === "string") this.setActivePreset(partial.active_preset);
		this.syncControls();
		this.updateAutoLabels();
		this.refreshDisabled();
		this.notify();
	}

	// ==================== Initialisation ====================

	/**
	 * Whether this server generates, once /api/status has said so.
	 *
	 * Everything below the check describes the machine that *runs* the model,
	 * and asks local-only routes (auto-preset, compatibility, profiles) about
	 * it. On the hosted site that machine is the user's, which applies its own
	 * memory settings (the hosted payload deliberately carries none), and those
	 * routes are 404s: asking them put a warning toast on every page load.
	 */
	private async serverGeneratesOnceKnown(): Promise<boolean> {
		if (store.get().status) return serverGenerates();
		return new Promise((resolve) => {
			const timer = window.setTimeout(() => {
				off();
				resolve(serverGenerates());
			}, 15000);
			const off = store.subscribe(
				(s) => s.status,
				(status) => {
					if (!status) return;
					window.clearTimeout(timer);
					off();
					resolve(serverGenerates());
				}
			);
		});
	}

	private async init(): Promise<void> {
		if (!(await this.serverGeneratesOnceKnown())) {
			setChildren(
				this.element,
				h(
					"p",
					{ class: "muted" },
					"Sur ce site, votre ordinateur choisit lui-même ses réglages mémoire, d'après sa " +
						"propre carte graphique — rien à régler ici."
				)
			);
			return;
		}
		let techniques: unknown[];
		let presets: Record<string, OptimizationPreset>;
		let profiles: OptimizationProfile[];
		try {
			[techniques, presets, profiles] = await Promise.all([
				APIClient.getAvailableOptimizations(),
				APIClient.getAllOptimizationPresets(),
				// Saved profiles are a local-only route (a 404 on the hosted
				// site). Inside Promise.all its failure took the whole panel down
				// and put an error toast on every hosted page load; no saved
				// profile is simply an empty list.
				APIClient.getOptimizationProfiles().catch(() => []),
			]);
		} catch (error) {
			toast(
				error instanceof Error ? error.message : "Réglages d'optimisation indisponibles.",
				"error"
			);
			setChildren(
				this.element,
				h("p", { class: "muted" }, "Réglages d'optimisation indisponibles pour le moment.")
			);
			return;
		}
		this.techniques = techniques as OptimizationTechnique[];
		this.presets = presets;
		this.profiles = profiles;

		// Le store fait foi s'il porte déjà des options (autre panneau,
		// session restaurée) ; sinon on part des defaults du backend.
		const stored = store.get().optimizationOptions;
		const hasStored = stored !== null && Object.keys(stored).length > 0;
		if (hasStored) {
			this.options = { ...stored } as OptimizationOptions;
			if (typeof stored.active_preset === "string") this.setActivePreset(stored.active_preset);
		} else {
			this.options = this.defaultOptions();
		}

		this.renderCategories();
		this.renderProfileOptions();
		this.loadingNote.remove();

		if (hasStored) {
			this.syncControls();
			this.updateAutoLabels();
			this.refreshDisabled();
			this.notify();
		} else {
			await this.applyPreset(this.activePreset);
		}
		this.ready = true;
		await this.refreshCompatibility(this.currentArch());
	}

	private defaultOptions(): OptimizationOptions {
		const defaults: Record<string, unknown> = {};
		for (const tech of this.techniques) defaults[tech.id] = tech.default;
		return defaults as OptimizationOptions;
	}

	// ==================== Rendu ====================

	private renderCategories(): void {
		const accordions: HTMLElement[] = [];
		const byCategory = new Map<string, OptimizationTechnique[]>();
		for (const tech of this.techniques) {
			const list = byCategory.get(tech.category) ?? [];
			list.push(tech);
			byCategory.set(tech.category, list);
		}
		const categories = [
			...CATEGORY_ORDER.filter((category) => byCategory.has(category)),
			...[...byCategory.keys()].filter((category) => !CATEGORY_ORDER.includes(category)),
		];
		for (const category of categories) {
			const meta = CATEGORY_META[category] ?? {
				label: category.charAt(0).toUpperCase() + category.slice(1),
			};
			const rows = (byCategory.get(category) ?? []).map((tech) => this.buildRow(tech));
			accordions.push(
				accordion({
					title: meta.label,
					children: rows,
					open: category === "engine" || category === "memory",
				}).element
			);
		}
		setChildren(this.categoriesContainer, ...accordions);
	}

	private buildRow(tech: OptimizationTechnique): HTMLElement {
		const control = this.buildControl(tech);
		this.controls.set(tech.id, control);

		const impact = h(
			"span",
			{ class: "opt-impact" },
			`VRAM : ${tech.vram_impact} · Vitesse : ${tech.speed_impact}`
		);
		const row = h(
			"div",
			{
				class: "opt-technique",
				title: tech.description,
				"data-tech": tech.id,
			},
			h(
				"div",
				{ class: "opt-meta" },
				h("span", { class: "opt-name" }, tech.name),
				h("span", { class: "opt-desc" }, tech.description),
				impact
			),
			h("div", { class: "opt-control" }, control.input.closest(".form-group") ?? control.input)
		);
		this.rows.set(tech.id, row);
		return row;
	}

	private buildControl(tech: OptimizationTechnique): ControlEntry {
		const current = this.options[tech.id] ?? tech.default;

		if (tech.options && tech.options.length > 0) {
			const field = selectField<string>({
				options: tech.options.map((value) => ({ value, label: optionLabel(tech.id, value) })),
				value: String(current ?? ""),
				onChange: (value) => this.updateOption(tech.id, value),
			});
			const input = field.element.querySelector("select") as HTMLSelectElement;
			return { input, setValue: (value) => field.setValue(String(value ?? "")) };
		}
		if (typeof tech.min === "number" && typeof tech.max === "number") {
			const field = sliderField({
				min: tech.min,
				max: tech.max,
				step: tech.step ?? 1,
				value: Number(current ?? tech.default ?? tech.min),
				onInput: (value) => this.updateOption(tech.id, value),
			});
			const input = field.element.querySelector("input") as HTMLInputElement;
			return { input, setValue: (value) => field.setValue(Number(value)) };
		}
		if (typeof tech.default === "boolean") {
			const field = checkboxField({
				label: "",
				checked: Boolean(current),
				onChange: (checked) => this.updateOption(tech.id, checked),
			});
			const input = field.element.querySelector("input") as HTMLInputElement;
			return { input, setValue: (value) => field.setValue(Boolean(value)) };
		}
		if (typeof tech.default === "number") {
			const field = numberField({
				value: Number(current ?? tech.default),
				onInput: (value) => this.updateOption(tech.id, value),
			});
			const input = field.element.querySelector("input") as HTMLInputElement;
			return { input, setValue: (value) => field.setValue(Number(value)) };
		}
		const field = selectField<string>({
			options: [{ value: String(current ?? ""), label: String(current ?? "") }],
			value: String(current ?? ""),
			onChange: (value) => this.updateOption(tech.id, value),
		});
		const input = field.element.querySelector("select") as HTMLSelectElement;
		return { input, setValue: (value) => field.setValue(String(value ?? "")) };
	}

	// ==================== Valeurs ====================

	private updateOption(id: string, value: string | number | boolean): void {
		if (this.options[id] === value) return;
		this.options = { ...this.options, [id]: value };
		// Une modification manuelle sort du preset actif.
		if (this.activePreset !== "custom") this.setActivePreset("custom");
		this.refreshDisabled();
		this.notify();
	}

	private syncControls(): void {
		for (const tech of this.techniques) {
			const control = this.controls.get(tech.id);
			if (control) control.setValue(this.options[tech.id] ?? tech.default);
		}
	}

	/** "Auto → <résolu>" : affiche ce que le planificateur choisit vraiment. */
	private updateAutoLabels(): void {
		for (const tech of this.techniques) {
			if (!tech.options?.includes("auto")) continue;
			const control = this.controls.get(tech.id);
			if (!(control?.input instanceof HTMLSelectElement)) continue;
			const autoOption = Array.from(control.input.options).find(
				(option) => option.value === "auto"
			);
			if (!autoOption) continue;
			const current = this.options[tech.id];
			const resolved = this.effectiveValues[`effective_${tech.id}`];
			autoOption.textContent =
				current === "auto" && resolved && resolved !== "auto"
					? `Auto → ${String(resolved)}`
					: optionLabel(tech.id, "auto");
		}
	}

	/** Un réglage dont le paquet n'est pas sur la machine qui exécutera.
	 *
	 * On le demande à **elle**, pas au serveur : sur le site hébergé l'API n'a
	 * pas de GPU et n'exécute rien, et une machine installée avant qu'un
	 * paquet soit livré ne l'a pas — republier un runtime entier pour
	 * quelques centaines de kilo-octets ferait retélécharger jusqu'à 3 Go à
	 * tout le monde. C'est la même règle que pour les modèles détenus.
	 */
	private missingPackage(tech: OptimizationTechnique): string | null {
		if (!tech.requires_package) return null;
		const target = store.get().executionTarget;
		if (target.kind === "hosted") {
			const has = target.worker.optional_accelerations ?? [];
			return has.includes(tech.requires_package) ? null : tech.requires_package;
		}
		return tech.available_here === false ? tech.requires_package : null;
	}

	/** Grise les techniques non supportées par l'architecture / le parent. */
	private refreshDisabled(): void {
		const arch = store.get().selectedModel?.architecture?.toLowerCase() ?? null;
		for (const tech of this.techniques) {
			const control = this.controls.get(tech.id);
			const row = this.rows.get(tech.id);
			if (!control || !row) continue;
			let disabled = false;
			let reason = "";
			if (tech.parent_id && !this.options[tech.parent_id]) {
				disabled = true;
				reason = "Nécessite l'activation de l'option parente.";
			}
			if (!disabled && tech.supported_archs?.length && arch) {
				if (!tech.supported_archs.includes(arch)) {
					disabled = true;
					reason = `Non pris en charge par l'architecture ${arch}.`;
				}
			}
			if (!disabled) {
				const missing = this.missingPackage(tech);
				if (missing) {
					disabled = true;
					reason =
						"Pas encore disponible sur votre ordinateur : ce réglage arrivera avec une prochaine mise à jour du programme.";
				}
			}
			control.input.disabled = disabled;
			row.classList.toggle("disabled-opt", disabled);
			row.title = reason || tech.description;
		}
		this.refreshIncompatibleOptions();
	}

	/** Valeurs rendues impossibles par le champ apparié (ex. NF4 + disque) :
	 *  les options incompatibles sont désactivées dans le select de
	 *  quantification, et une valeur devenue invalide est replacée sur la
	 *  première option encore autorisée. */
	private refreshIncompatibleOptions(): void {
		for (const tech of this.techniques) {
			if (!tech.incompatible_with) continue;
			const pairedId = tech.id.replace(/_device$/, "_quantization");
			const paired = this.controls.get(pairedId);
			if (!(paired?.input instanceof HTMLSelectElement)) continue;
			const deviceValue = String(this.options[tech.id] ?? tech.default ?? "");
			const blocked = tech.incompatible_with[deviceValue] ?? [];
			for (const option of Array.from(paired.input.options)) {
				option.disabled = blocked.includes(option.value);
			}
			const currentQuant = String(this.options[pairedId] ?? "");
			if (blocked.includes(currentQuant)) {
				const fallback = Array.from(paired.input.options).find((option) => !option.disabled)?.value;
				if (fallback && fallback !== currentQuant) this.updateOption(pairedId, fallback);
			}
		}
	}

	private notify(): void {
		const snapshot = this.getOptions();
		this.onChange?.(snapshot);
		store.setState({ optimizationOptions: snapshot });
	}

	// ==================== Presets ====================

	private setActivePreset(name: string): void {
		this.activePreset = name;
		for (const [presetName, button] of this.presetButtons) {
			button.classList.toggle("active", presetName === name);
		}
	}

	private async applyPreset(name: string): Promise<void> {
		this.setActivePreset(name);
		if (name === "custom") {
			this.notify();
			return;
		}
		if (name === "auto") {
			await this.applyAutoPreset();
			return;
		}
		const preset = this.presets[name];
		if (!preset?.options) {
			this.notify();
			return;
		}
		const next = { ...this.options };
		for (const [key, value] of Object.entries(preset.options)) {
			const tech = this.techniques.find((item) => item.id === key);
			// Les boot params restent tels quels : ils se règlent au chargement.
			if (tech && !tech.is_boot_param) (next as Record<string, unknown>)[key] = value;
		}
		this.options = next;
		this.syncControls();
		this.updateAutoLabels();
		this.refreshDisabled();
		this.notify();
	}

	private async applyAutoPreset(): Promise<void> {
		const token = ++this.archToken;
		const arch = this.currentArch();
		try {
			const response = await APIClient.getAutoPreset(arch, "balanced");
			if (token !== this.archToken) return;
			const next = { ...this.options };
			// Boot params : on garde "auto" — le planificateur les résout à la
			// génération (au poids de la résolution demandée), les épingler
			// ici gèlerait l'échelle d'escalade anti-OOM.
			for (const tech of this.techniques) {
				if (tech.is_boot_param) (next as Record<string, unknown>)[tech.id] = tech.default ?? "auto";
			}
			for (const [key, value] of Object.entries(response.optimal_settings)) {
				const tech = this.techniques.find((item) => item.id === key);
				if (tech && !tech.is_boot_param) {
					// Champ supportant "auto" : on le garde sur "auto".
					(next as Record<string, unknown>)[key] = tech.options?.includes("auto") ? "auto" : value;
				}
			}
			this.options = next;
		} catch {
			if (token === this.archToken) {
				toast("Réglages automatiques indisponibles — valeurs actuelles conservées.", "warning");
			}
		}
		this.syncControls();
		this.updateAutoLabels();
		this.refreshDisabled();
		this.notify();
	}

	private resetToDefaults(): void {
		this.setActivePreset("custom");
		this.options = this.defaultOptions();
		this.syncControls();
		this.updateAutoLabels();
		this.refreshDisabled();
		this.notify();
	}

	// ==================== Profils ====================

	private renderProfileOptions(): void {
		this.profileSelect.replaceChildren(
			h("option", { value: "" }, "Choisir un profil…"),
			...this.profiles.map((profile) => h("option", { value: profile.name }, profile.name))
		);
		this.profileSelect.value = "";
		this.profileDeleteButton.disabled = true;
	}

	private async applySelectedProfile(): Promise<void> {
		const name = this.profileSelect.value;
		this.profileDeleteButton.disabled = !name;
		if (!name || !this.ready) return;
		const profile = this.profiles.find((item) => item.name === name);
		if (!profile) return;
		this.setActivePreset("custom");
		this.setOptions(profile.options);
		this.profileStatus.textContent = `Profil « ${profile.name} » chargé.`;
	}

	private async saveProfile(): Promise<void> {
		const name = this.profileNameInput.value.trim();
		if (!name) {
			toast("Indiquez un nom de profil.", "warning");
			this.profileNameInput.focus();
			return;
		}
		try {
			const saved = await APIClient.saveOptimizationProfile(name, this.getOptions());
			const index = this.profiles.findIndex(
				(profile) => profile.name.toLowerCase() === saved.name.toLowerCase()
			);
			if (index >= 0) this.profiles[index] = saved;
			else this.profiles.push(saved);
			this.profiles.sort((a, b) => a.name.localeCompare(b.name));
			this.renderProfileOptions();
			this.profileSelect.value = saved.name;
			this.profileDeleteButton.disabled = false;
			this.profileStatus.textContent = `Profil « ${saved.name} » enregistré.`;
			toast(`Profil « ${saved.name} » enregistré.`, "success");
		} catch (error) {
			toast(
				error instanceof Error ? error.message : "Impossible d'enregistrer ce profil.",
				"error"
			);
		}
	}

	private async deleteProfile(): Promise<void> {
		const name = this.profileSelect.value;
		if (!name) return;
		const ok = await confirmDialog("Supprimer le profil", `Supprimer le profil « ${name} » ?`);
		if (!ok) return;
		try {
			await APIClient.deleteOptimizationProfile(name);
			this.profiles = this.profiles.filter((profile) => profile.name !== name);
			this.renderProfileOptions();
			this.profileStatus.textContent = `Profil « ${name} » supprimé.`;
			toast(`Profil « ${name} » supprimé.`, "success");
		} catch (error) {
			toast(error instanceof Error ? error.message : "Impossible de supprimer ce profil.", "error");
		}
	}

	// ==================== Compatibilité & architecture ====================

	private currentArch(): string {
		return store.get().selectedModel?.architecture?.toLowerCase() ?? "sd15";
	}

	private async onArchitectureChange(arch: string | null): Promise<void> {
		if (!this.ready) return;
		this.refreshDisabled();
		await this.refreshCompatibility(arch ?? "sd15");
		// Preset auto : recalculer les réglages optimaux pour la nouvelle
		// architecture (comportement de l'ancien setTargetArchitecture).
		if (this.activePreset === "auto") await this.applyAutoPreset();
	}

	private async refreshCompatibility(arch: string): Promise<void> {
		const token = ++this.compatToken;
		try {
			const info = await APIClient.getCompatibilityInfo(arch);
			if (token !== this.compatToken) return;
			const lines: HTMLElement[] = [];
			if (!info.is_compatible) {
				lines.push(
					h("p", { class: "compat-bad" }, "Matériel insuffisant pour cette architecture.")
				);
			}
			for (const warning of info.warnings) {
				lines.push(h("p", { class: "compat-warning" }, warning));
			}
			for (const recommendation of info.recommendations) {
				lines.push(h("p", { class: "compat-rec" }, recommendation));
			}
			if (lines.length === 0) {
				this.compatBox.hidden = true;
				return;
			}
			lines.push(
				h("p", { class: "compat-res" }, `Résolution max de référence : ${info.max_resolution} px`)
			);
			setChildren(this.compatBox, ...lines);
			this.compatBox.hidden = false;
		} catch {
			if (token === this.compatToken) this.compatBox.hidden = true;
		}
	}
}
